import type { PathCommand } from '@/lib/elements/types/path-command'
import type { TightBounds } from '../types/tight-bounds'

/** Below this, a Bézier derivative coefficient is treated as zero (degenerate/linear segment). */
const DERIVATIVE_EPSILON = 1e-10

/**
 * Parameters in (0,1) where a cubic Bézier component reaches a local extremum — the roots of its
 * derivative. Same formulation as `pathTightBounds` in `lib/elements/pen-path-utils.ts`, kept
 * identical so pen paths and generated shape paths never disagree about where a curve turns.
 */
export function cubicExtremaParams(p0: number, p1: number, p2: number, p3: number): number[] {
  const a = -p0 + 3 * p1 - 3 * p2 + p3
  const b = 2 * (p0 - 2 * p1 + p2)
  const c = p1 - p0
  const ts: number[] = []

  if (Math.abs(a) < DERIVATIVE_EPSILON) {
    if (Math.abs(b) > DERIVATIVE_EPSILON) {
      const t = -c / b

      if (t > 0 && t < 1) ts.push(t)
    }

    return ts
  }

  const disc = b * b - 4 * a * c

  if (disc < 0) return ts

  const sq = Math.sqrt(disc)

  for (const t of [(-b + sq) / (2 * a), (-b - sq) / (2 * a)]) {
    if (t > 0 && t < 1) ts.push(t)
  }

  return ts
}

/**
 * Parameter in (0,1) where a quadratic Bézier component reaches its extremum. Quadratics are
 * solved directly rather than degree-elevated to a cubic: `CurveToCommand` distinguishes Q from C
 * by the absence of a second control point (see `flattenPathCommandsToPolygon` in
 * `lib/text/shape-contour-wrap.ts`), and duplicating the single control point into both cubic
 * slots would describe a different curve.
 */
export function quadraticExtremaParams(p0: number, p1: number, p2: number): number[] {
  const denom = p0 - 2 * p1 + p2

  if (Math.abs(denom) < DERIVATIVE_EPSILON) return []

  const t = (p0 - p1) / denom

  return t > 0 && t < 1 ? [t] : []
}

function cubicAt(p0: number, p1: number, p2: number, p3: number, t: number): number {
  const u = 1 - t

  return u * u * u * p0 + 3 * u * u * t * p1 + 3 * u * t * t * p2 + t * t * t * p3
}

function quadraticAt(p0: number, p1: number, p2: number, t: number): number {
  const u = 1 - t

  return u * u * p0 + 2 * u * t * p1 + t * t * p2
}

/**
 * Tight bounds of a path's actual drawn geometry — the curve itself, not the convex hull of its
 * control points.
 *
 * This is deliberately different from `pathCommandsBBox` in `lib/elements/shape-path-commands.ts`,
 * which includes control points and therefore reports a box that can be substantially larger than
 * the drawn curve (a 4-segment circle's control points sit outside the circle). Control points are
 * never on the curve, so they are excluded here and interior extrema are solved for instead.
 *
 * Stroke width is not considered: these are path-geometry bounds. Callers that need the inked
 * extent inflate by half the stroke width themselves.
 */
export function pathCommandsTightBounds(commands: PathCommand[]): TightBounds | null {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  let seen = false

  const addX = (x: number) => {
    if (!Number.isFinite(x)) return
    if (x < minX) minX = x
    if (x > maxX) maxX = x
    seen = true
  }

  const addY = (y: number) => {
    if (!Number.isFinite(y)) return
    if (y < minY) minY = y
    if (y > maxY) maxY = y
    seen = true
  }

  const addPoint = (x: number, y: number) => {
    addX(x)
    addY(y)
  }

  // Matches `flattenPathCommandsToPolygon`: commands are authored with a leading moveTo, but a
  // path that omits one starts from the local origin rather than being dropped.
  let currentX = 0
  let currentY = 0

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      currentX = cmd.moveTo.x
      currentY = cmd.moveTo.y
      addPoint(currentX, currentY)
      continue
    }

    if ('lineTo' in cmd) {
      currentX = cmd.lineTo.x
      currentY = cmd.lineTo.y
      addPoint(currentX, currentY)
      continue
    }

    if ('curveTo' in cmd) {
      const ct = cmd.curveTo
      const p0x = currentX
      const p0y = currentY
      const c1x = ct.controlX
      const c1y = ct.controlY
      // In-memory second control is absolute: the decoder writes wire `deltaX/deltaY` (which are
      // center-relative on the wire) straight into `deltaX/deltaY` after `offsetPathCommands`
      // re-bases the whole path to top-left. Same resolution order as `pathCommandsBBox`.
      const c2x = ct.controlX2 ?? ct.deltaX
      const c2y = ct.controlY2 ?? ct.deltaY
      const isCubic = typeof c2x === 'number' && typeof c2y === 'number'

      addPoint(p0x, p0y)
      addPoint(ct.x, ct.y)

      if (isCubic) {
        for (const t of cubicExtremaParams(p0x, c1x, c2x, ct.x)) {
          addX(cubicAt(p0x, c1x, c2x, ct.x, t))
        }

        for (const t of cubicExtremaParams(p0y, c1y, c2y, ct.y)) {
          addY(cubicAt(p0y, c1y, c2y, ct.y, t))
        }
      } else {
        for (const t of quadraticExtremaParams(p0x, c1x, ct.x)) {
          addX(quadraticAt(p0x, c1x, ct.x, t))
        }

        for (const t of quadraticExtremaParams(p0y, c1y, ct.y)) {
          addY(quadraticAt(p0y, c1y, ct.y, t))
        }
      }

      currentX = ct.x
      currentY = ct.y
    }
  }

  if (!seen || minX === Infinity || minY === Infinity) return null

  return { minX, minY, maxX, maxY }
}
